import { Component, Inject, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import {
	AppModalService,
	ModalData,
} from '@datachain/ui-sdk/components/app-modal';
import { FieldConfig } from '@datachain/ui-sdk/components/query-builder';
import { randUuid } from '@ngneat/falso';
import { Map } from 'immutable';
import { BehaviorSubject, map, Observable } from 'rxjs';

import { DcIcons } from '../../../../ui/app-dc.icons';
import { ValidationErrorKeys } from '../../../../ui/form/validation-erros-keys';
import { ColumnConfigItemEntity } from '../../../domain/column-config-item.entity';
import {
	SubscriptionDiagnosticError,
	SubscriptionErrorKey,
} from '../../../domain/diagnostic-errors.entity';
import { FilterModelItemEntity } from '../../../domain/filter-model-item.entity';
import { ColumnsQueryFieldsProvider } from './columns-query-fields-provider.service';
import {
	FilterModelFormControls,
	FilterModelMetadataForm,
} from './filter-model-metadata.form';

export enum FilterModelConfigAction {
	Create = 'create',
	Update = 'update',
}

// TODO: missing SubscriptionProvider to mutualize error handling

@Component({
	selector: 'app-filter-model-config-modal',
	templateUrl: './filter-model-config-modal.component.html',
	styleUrls: ['./filter-model-config-modal.component.scss'],
	providers: [FilterModelMetadataForm, ColumnsQueryFieldsProvider],
})
export class FilterModelConfigModalComponent<
		T extends {
			filterModelConfig: FilterModelItemEntity;
			availableColumns: Map<string, ColumnConfigItemEntity>;
			action: FilterModelConfigAction;
		}
	>
	extends DcBaseComponent
	implements OnInit
{
	protected readonly DcIcons = DcIcons;
	protected readonly FilterModelFormControls = FilterModelFormControls;
	protected readonly ValidationErrorKeys = ValidationErrorKeys;
	protected readonly Appearance: MatFormFieldAppearance = 'legacy';

	public availableFields!: Array<FieldConfig>;
	public queryFormGroup!: FormGroup;

	public vo$: Observable<{
		error: SubscriptionDiagnosticError | undefined;
		criticErrors: number;
	}>;

	private readonly errors$ = new BehaviorSubject<
		Map<SubscriptionErrorKey, SubscriptionDiagnosticError>
	>(Map());

	public constructor(
		public readonly filterConfigForm: FilterModelMetadataForm,
		private readonly modalService: AppModalService,
		private readonly columnsQueryBuilderProvider: ColumnsQueryFieldsProvider,
		@Inject(MAT_DIALOG_DATA) private readonly modalData: ModalData & T
	) {
		super();
		this.cmpId = 'filter-model-configuration';

		this.vo$ = combineAll({
			error: this.errors$.pipe(map((errors) => errors.valueSeq().first())),
			criticErrors: this.errors$.pipe(
				map((errors) => errors.filter((error) => error.type === 'critic').size)
			),
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();

		const filtered = this.modalData.availableColumns.filter(
			(v) => !v.isList && v.isSelectedInSource
		);
		const fields =
			this.columnsQueryBuilderProvider.convertToQueryBuilderField(filtered);
		this.availableFields = fields;

		if (this.modalData.action === FilterModelConfigAction.Update) {
			this.filterConfigForm.populate(this.modalData.filterModelConfig);
			this.queryFormGroup =
				this.columnsQueryBuilderProvider.populateQueryBuilderForm(
					this.modalData.filterModelConfig.root,
					fields
				);
		} else {
			this.queryFormGroup =
				this.columnsQueryBuilderProvider.createInitialQueryBuilderForm(fields);

			this.filterConfigForm.form
				.get(FilterModelFormControls.Label)
				?.markAsTouched();
		}
	}

	public upsertFilterModel(): void {
		const filterForm = this.filterConfigForm.extract();
		const newFilterModel = FilterModelItemEntity.build({
			id:
				this.modalData.action === FilterModelConfigAction.Create
					? randUuid()
					: filterForm.id,
			label: filterForm.label,
			description: filterForm.description,
			root: this.queryFormGroup.value,
		});

		this.modalService.closeActiveModal({
			model: newFilterModel,
		});
	}
}
